import React, {
  useState, useMemo, useEffect,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  message, notification, Progress, Select,
} from 'antd';
import * as XLSX from 'xlsx';
import { withScriptjs } from 'react-google-maps';

import BreadcrumbContainer from '../common/breadcrumbContainer/breadcrumbContainer';
import StepsContainer from '../common/containers/StepsContainer';
import SheetUploadPrompt from '../common/excel/SheetUploadPrompt';
import SheetHeaderMatcher from '../common/excel/SheetHeaderMatcher';
import DrawerSubmitFooter from '../common/containers/DrawerSubmitFooter';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import MassImportSheetSelector from './MassImportSheetSelector';
import MassImportPreview from './MassImportPreview';
import MassImportResults from './MassImportResults';
import { getIdMap } from '../helpers/helpers';
import { EQUIPMENT_CATEGORY_ID, MATERIAL_CATEGORY_ID } from '../helpers/costcodeHelpers';
import { IMPORT_TYPE_CONFIGS, IMPORT_TYPES } from './massImport.constants';
import { executeImport } from './importHelpers';
import {
  getCustomFieldHeaders,
  getBoardIdsFromCustomFieldSections,
} from './customFieldImportHelpers';
import { getBucketTemplates, getBucketTemplateStatuses } from '../buckets/state/buckets.actions';
import { getTemplates } from '../forms/state/forms.actions';
import { getScheduleCategories } from '../schedule/state/schedule.actions';
import { getProjects, getProjectCustomFieldTemplate } from '../projects/state/projects.actions';
import { getBoardCardsForDropdown } from '../boards/state/boards.actions';
import { getCustomerCustomFieldTemplate } from '../contacts/customers/state/customers.actions';
import {
  getAllPhases,
  getCostcodeCustomFieldTemplate,
  searchCostcodeOptions,
} from '../costcodes/state/costcodes.actions';
import { getUserCustomFieldTemplate } from '../users/state/users.actions';
import { getStatuses as getEquipmentStatuses, getEquipmentTypes } from '../equipment/state/equipment.actions';
import { getUnions } from '../unions/state/unions.actions';
import { getMaterials } from '../materials/state/materials.actions';
import { getGlobalMaterialLocations } from '../globalMaterialLocations/state/globalMaterialLocations.actions';

const DEFAULT_CRUMBS = [{
  text: 'Import',
  icon: 'import',
}];

function MassImport({ history, match }) {
  const dispatch = useDispatch();
  const importType = match?.params?.type || null;
  const importTypeConfig = IMPORT_TYPE_CONFIGS[importType];
  const isMaterial = importType === IMPORT_TYPES.MATERIALS;
  const isEquipment = importType === IMPORT_TYPES.EQUIPMENT;

  const divisions = useSelector((state) => state.settings.divisions);
  const customers = useSelector((state) => state.customers.customers);
  const projectTypes = useSelector((state) => state.projects.projectTypes);
  const costcodeCategories = useSelector((state) => state.costcodes.categories);
  const phases = useSelector((state) => state.costcodes.phases);
  const equipmentStatuses = useSelector((state) => state.equipment.statuses);
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);
  const equipment = useSelector((state) => state.equipment.equipment);
  const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);
  const bucketStatuses = useSelector((state) => state.buckets.statuses);
  const projects = useSelector((state) => state.projects.projects);
  const customerCustomFields = useSelector((state) => state.customers.customFieldTemplate);
  const projectCustomFields = useSelector((state) => state.projects.customFieldTemplate);
  const costcodeCustomFields = useSelector((state) => state.costcodes.customFieldTemplate);
  const userCustomFields = useSelector((state) => state.users.customFieldTemplate);
  const users = useSelector((state) => state.users.users);
  const vendors = useSelector((state) => state.vendors.vendors);
  const labels = useSelector((state) => state.labels);
  const globalAddressBooks = useSelector((state) => state.contacts.globalAddressBooks);
  const formTemplates = useSelector((state) => state.forms.templates);
  const positionNames = useSelector((state) => state.settings.positionNames);
  const boardCardsForDropdown = useSelector((state) => state.boards.boardCardsForDropdown);
  const scheduleCategories = useSelector((state) => state.schedule.categories);
  const scheduleOfValues = useSelector((state) => state.projects.scheduleOfValues);
  const companyTimezone = useSelector((state) => state.settings.company.settings.timezone);
  const materials = useSelector((state) => state.materials.materials);
  const globalMaterialLocations = useSelector((state) => (
    state.globalMaterialLocations?.globalMaterialLocations
  ));
  const {
    unions: unionList = [],
    locals: unionLocals = [],
    classes: unionClasses = [],
  } = useSelector((state) => state.unions);

  const allDivisionIds = useMemo(() => (
    Object.values(divisions).map((division) => division.id)
  ), [divisions]);

  const divisionMap = useMemo(() => {
    const map = {};
    Object.values(divisions).forEach((division) => {
      map[division.name] = division.id;
    });
    return map;
  }, [divisions]);

  const [costcodeOptions, setCostcodeOptions] = useState([]);

  // costcode code/name.
  const costcodeMap = useMemo(() => {
    const map = {};
    costcodeOptions.forEach(({ value, code, name }) => {
      const costcode = { id: value, code, name };
      if (name) map[name] = costcode;
      if (code) map[code] = costcode;
    });
    return map;
  }, [costcodeOptions]);

  const materialMap = useMemo(
    () => getIdMap(Object.values(materials || {}), 'name'),
    [materials],
  );

  const globalMaterialLocationNameMap = useMemo(
    () => getIdMap(globalMaterialLocations || [], 'locationText'),
    [globalMaterialLocations],
  );

  // customer name
  const customerMap = useMemo(
    () => getIdMap(Object.values(customers || {}), 'name'),
    [customers],
  );

  // project type name
  const projectTypeMap = useMemo(
    () => getIdMap(projectTypes, 'name'),
    [projectTypes],
  );

  // costcode category name
  const categoryMap = useMemo(
    () => getIdMap(Object.values(costcodeCategories || {}), 'name'),
    [costcodeCategories],
  );

  // equipment status label
  const statusMap = useMemo(
    () => getIdMap(Object.values(equipmentStatuses || {}), 'status'),
    [equipmentStatuses],
  );

  // default equipment status id
  const defaultStatusId = useMemo(() => {
    const statuses = Object.values(equipmentStatuses || {});
    return statuses.length ? statuses[0].id : undefined;
  }, [equipmentStatuses]);

  // equipment type name
  const equipmentTypeMap = useMemo(
    () => getIdMap(equipmentTypes, 'name'),
    [equipmentTypes],
  );

  // equipment name/code
  const equipmentMap = useMemo(
    () => getIdMap(equipment, 'name'),
    [equipment],
  );

  const equipmentCodeMap = useMemo(() => {
    const map = {};
    (equipment || []).forEach((eq) => {
      if (eq.code) map[String(eq.code)] = eq;
    });
    return map;
  }, [equipment]);

  // bucket template name
  const bucketTemplateMap = useMemo(
    () => getIdMap(bucketTemplates, 'name'),
    [bucketTemplates],
  );

  // project name
  const projectMap = useMemo(
    () => getIdMap(Object.values(projects || {}), 'name'),
    [projects],
  );

  // user name/email/username
  const userMap = useMemo(() => {
    const map = {};
    Object.values(users || {}).forEach((user) => {
      if (user.name) map[user.name] = user;
      if (user.email) map[user.email] = user;
      if (user.username) map[user.username] = user;
    });
    return map;
  }, [users]);

  const employeeIdMap = useMemo(() => {
    const map = {};
    Object.values(users || {}).forEach((user) => {
      if (user.employeeId) map[user.employeeId] = user;
    });
    return map;
  }, [users]);

  // label title (all types)
  const labelMap = useMemo(
    () => getIdMap(labels, 'title'),
    [labels],
  );

  // project labels only
  const projectLabelMap = useMemo(
    () => getIdMap((labels || []).filter((l) => l.type === 'projects'), 'title'),
    [labels],
  );

  // equipment labels only
  const equipmentLabelMap = useMemo(
    () => getIdMap((labels || []).filter((l) => l.type === 'equipment'), 'title'),
    [labels],
  );

  // contact (address book) name
  const contactMap = useMemo(
    () => getIdMap(Object.values(globalAddressBooks || {}), 'name'),
    [globalAddressBooks],
  );

  // form template name
  const formTemplateMap = useMemo(
    () => getIdMap(Object.values(formTemplates || {}), 'name'),
    [formTemplates],
  );

  const scheduleCategoryMap = useMemo(
    () => getIdMap(scheduleCategories, 'name'),
    [scheduleCategories],
  );

  // role/position name
  const roleMap = useMemo(
    () => getIdMap(positionNames, 'name'),
    [positionNames],
  );

  // board card name
  const cardMap = useMemo(() => {
    const allCards = Object.values(boardCardsForDropdown || {})
      .flatMap((boardCards) => boardCards.cards || [])
      .filter((card) => card.name);

    // rename cards to just the name after the dash
    const renamedCards = allCards.map((card) => {
      if (card.name.includes('-')) {
        const cardName = card.name.split('-')[1].trim();
        return {
          ...card,
          name: cardName,
        };
      }
      return card;
    });

    return getIdMap(renamedCards, 'name');
  }, [boardCardsForDropdown]);

  // vendor name
  const vendorMap = useMemo(
    () => getIdMap(Object.values(vendors || {}), 'name'),
    [vendors],
  );

  const isMultiDivision = allDivisionIds.length > 1;

  const [currentStep, setCurrentStep] = useState(0);
  const [uploadFile, setUploadFile] = useState(null);
  const [selectedSheetName, setSelectedSheetName] = useState(null);
  const [headerMapping, setHeaderMapping] = useState({});
  const [selectedBucketTemplate, setSelectedBucketTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [useProjectCoordinates, setUseProjectCoordinates] = useState(false);

  // Selected bucket template object
  const selectedBucketTemplateObj = useMemo(() => {
    if (!selectedBucketTemplate || !bucketTemplates?.length) return null;
    return bucketTemplates.find((t) => t.id === selectedBucketTemplate) ?? null;
  }, [selectedBucketTemplate, bucketTemplates]);

  // bucket status map
  const bucketStatusMap = useMemo(() => {
    const selectedBucketStatus = bucketStatuses?.[selectedBucketTemplateObj?.id];
    return getIdMap(Object.values(selectedBucketStatus || {}), 'status');
  }, [selectedBucketTemplateObj, bucketStatuses]);

  // Collect board IDs from custom fields that use Cards dropdowns (for pre-fetching board cards)
  const boardIdsFromCustomFields = useMemo(() => {
    let relevantTemplate = null;
    if (importType === IMPORT_TYPES.CUSTOMERS) {
      relevantTemplate = customerCustomFields;
    } else if (importType === IMPORT_TYPES.PROJECTS) {
      relevantTemplate = projectCustomFields;
    } else if (importType === IMPORT_TYPES.COST_CODES) {
      relevantTemplate = costcodeCustomFields;
    } else if (importType === IMPORT_TYPES.USERS) {
      relevantTemplate = userCustomFields;
    } else if (importType === IMPORT_TYPES.BUCKETS && selectedBucketTemplateObj) {
      relevantTemplate = selectedBucketTemplateObj.customFields;
    }
    return getBoardIdsFromCustomFieldSections(relevantTemplate?.sections ?? []);
  }, [
    importType,
    customerCustomFields,
    projectCustomFields,
    costcodeCustomFields,
    userCustomFields,
    selectedBucketTemplateObj,
  ]);

  const effectiveHeaders = useMemo(() => {
    if (!importTypeConfig) return [];
    let { headers = [] } = importTypeConfig;

    // For buckets, add dynamic custom field headers based on selected bucket template
    const isBucketsWithTemplate = importType === IMPORT_TYPES.BUCKETS
      && selectedBucketTemplateObj?.customFields?.sections;
    if (isBucketsWithTemplate) {
      const { sections } = selectedBucketTemplateObj.customFields;
      const customFieldHeaders = getCustomFieldHeaders(sections);
      headers = [...headers, ...customFieldHeaders];
    }

    // For other profile types, add custom field headers from their templates
    if (importType === IMPORT_TYPES.CUSTOMERS && customerCustomFields?.sections) {
      const customFieldHeaders = getCustomFieldHeaders(customerCustomFields.sections);
      headers = [...headers, ...customFieldHeaders];
    } else if (importType === IMPORT_TYPES.PROJECTS && projectCustomFields?.sections) {
      const customFieldHeaders = getCustomFieldHeaders(projectCustomFields.sections);
      headers = [...headers, ...customFieldHeaders];
    } else if (importType === IMPORT_TYPES.COST_CODES && costcodeCustomFields?.sections) {
      const customFieldHeaders = getCustomFieldHeaders(costcodeCustomFields.sections);
      headers = [...headers, ...customFieldHeaders];
    } else if (importType === IMPORT_TYPES.USERS && userCustomFields?.sections) {
      const customFieldHeaders = getCustomFieldHeaders(userCustomFields.sections);
      headers = [...headers, ...customFieldHeaders];
    }

    if (isMultiDivision) return headers;
    return headers.filter((h) => h.key !== 'division');
  }, [
    importTypeConfig,
    isMultiDivision,
    importType,
    selectedBucketTemplateObj,
    customerCustomFields,
    projectCustomFields,
    costcodeCustomFields,
    userCustomFields,
    useProjectCoordinates,
  ]);

  const sheets = useMemo(() => {
    if (!uploadFile) return {};
    const { Sheets = {} } = uploadFile;
    return Sheets;
  }, [uploadFile]);

  const selectedSheet = useMemo(() => {
    if (!sheets || !selectedSheetName) return [];
    const { [selectedSheetName]: fullSheet = {} } = sheets;
    return XLSX.utils.sheet_to_json(fullSheet, { defval: '' });
  }, [sheets, selectedSheetName]);

  useEffect(() => {
    dispatch(getTemplates());
    dispatch(getScheduleCategories());
  }, []);

  const hasCostcodeCustomField = useMemo(
    () => effectiveHeaders.some((header) => header.dataType === 'Costcodes'),
    [effectiveHeaders],
  );

  useEffect(() => {
    if (hasCostcodeCustomField) dispatch(getAllPhases());
  }, [hasCostcodeCustomField]);

  useEffect(() => {
    if (!isMaterial && !isEquipment && !hasCostcodeCustomField) return;

    const loadCostcodeOptions = async () => {
      const options = await dispatch(searchCostcodeOptions({
        divisionIds: allDivisionIds,
        onlyActive: false,
        includeCodeAndName: true,
        ...(!hasCostcodeCustomField && {
          categoryIds: isMaterial ? [MATERIAL_CATEGORY_ID] : [EQUIPMENT_CATEGORY_ID],
        }),
      }));
      setCostcodeOptions(options);
    };
    loadCostcodeOptions();
  }, [isMaterial, isEquipment, hasCostcodeCustomField, allDivisionIds]);

  useEffect(() => {
    if (importType === IMPORT_TYPES.BUCKETS && selectedBucketTemplateObj) {
      dispatch(getBucketTemplateStatuses(selectedBucketTemplateObj.id));
    }
  }, [importType, selectedBucketTemplateObj]);

  useEffect(() => {
    if (!uploadFile) return;
    const { SheetNames = [] } = uploadFile;
    if (!selectedSheetName || (SheetNames.length === 1 && selectedSheetName !== SheetNames[0])) {
      setSelectedSheetName(SheetNames[0]);
    }
  }, [selectedSheetName, uploadFile]);

  useEffect(() => {
    if (boardIdsFromCustomFields.length > 0) {
      dispatch(getBoardCardsForDropdown(boardIdsFromCustomFields));
    }
  }, [boardIdsFromCustomFields]);

  useEffect(() => {
    if (importType === IMPORT_TYPES.CUSTOMERS) {
      dispatch(getCustomerCustomFieldTemplate());
    } else if (importType === IMPORT_TYPES.PROJECTS) {
      dispatch(getProjectCustomFieldTemplate());
    } else if (importType === IMPORT_TYPES.COST_CODES) {
      dispatch(getCostcodeCustomFieldTemplate());
    } else if (importType === IMPORT_TYPES.USERS) {
      dispatch(getUserCustomFieldTemplate());
    } else if (importType === IMPORT_TYPES.BULK_USER_UPDATE) {
      dispatch(getUnions());
    } else if (importType === IMPORT_TYPES.EQUIPMENT) {
      dispatch(getEquipmentStatuses());
      dispatch(getEquipmentTypes());
    } else if (importType === IMPORT_TYPES.BUCKETS) {
      dispatch(getBucketTemplates());
      dispatch(getProjects({}));
    } else if (importType === IMPORT_TYPES.MATERIALS) {
      dispatch(getMaterials());
    } else if (importType === IMPORT_TYPES.BULK_MATERIAL_UPDATE) {
      dispatch(getMaterials());
      dispatch(getGlobalMaterialLocations());
    }
  }, [importType]);

  const onFileChange = (workbook) => {
    setUploadFile(workbook);
    setCurrentStep(1);
  };

  const updateProgressNotification = ({ percent, current, total }) => {
    notification.open({
      key: 'import-progress',
      message: `Importing ${importTypeConfig?.label || 'Data'}`,
      description: (
        <div style={{ padding: 16 }}>
          <Progress
            percent={percent}
            status="active"
            format={() => `${current} / ${total}`}
          />
        </div>
      ),
      duration: 0,
    });
  };

  const updateAfterImport = async () => {
    if (importType === IMPORT_TYPES.BULK_MATERIAL_UPDATE) {
      await dispatch(getGlobalMaterialLocations());
    }
  };

  const runImport = async (extraContext = {}) => {
    setLoading(true);
    updateProgressNotification({ percent: 0, current: 0, total: 0 });

    let contextLabelMap = labelMap;
    if (importType === IMPORT_TYPES.PROJECTS) contextLabelMap = projectLabelMap;
    else if (importType === IMPORT_TYPES.EQUIPMENT) contextLabelMap = equipmentLabelMap;

    try {
      const results = await executeImport(
        importType,
        selectedSheet,
        headerMapping,
        effectiveHeaders,
        {
          allDivisionIds,
          divisionMap,
          costcodeMap,
          costcodeOptions,
          phases,
          importType,
          materialMap,
          customerMap,
          projectTypeMap,
          categoryMap,
          statusMap,
          globalMaterialLocationNameMap,
          defaultStatusId,
          equipmentTypeMap,
          equipmentMap,
          equipmentCodeMap,
          bucketTemplateMap,
          projectMap,
          bucketStatusMap,
          selectedBucketTemplate: selectedBucketTemplateObj,
          userMap,
          employeeIdMap,
          useProjectCoordinates,
          unions: unionList,
          unionLocals,
          unionClasses,
          bulkUserUpdateSeenNames: new Set(),
          bulkUserUpdateSeenEmployeeIds: new Set(),
          bulkMaterialUpdateSeenNames: new Set(),
          bulkEquipmentUpdateSeenNames: new Set(),
          bulkEquipmentUpdateSeenCodes: new Set(),
          vendorMap,
          labelMap: contextLabelMap,
          contactMap,
          formTemplateMap,
          roleMap,
          cardMap,
          boardCardsForDropdown,
          customerCustomFields,
          projectCustomFields,
          costcodeCustomFields,
          userCustomFields,
          scheduleCategoryMap,
          scheduleOfValues,
          timezone: companyTimezone || 'America/Los_Angeles',
          ...(!isMultiDivision && { defaultDivisionId: allDivisionIds[0] }),
          ...extraContext,
        },
        updateProgressNotification,
      );

      notification.close('import-progress');

      const { success, failed } = results;

      // update the import results
      await updateAfterImport();

      setImportResults({ success, failed });
      setCurrentStep(4);
    } catch (error) {
      notification.close('import-progress');
      message.error(`Import failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const onNext = async () => {
    if (currentStep < 3) {
      setCurrentStep((prevStep) => prevStep + 1);
    } else if (importType === IMPORT_TYPES.USERS) {
      CustomConfirmModal({
        title: 'Send Welcome Emails?',
        content: 'Would you like to send welcome emails to all imported users? Each user will receive an email with their login credentials.',
        okText: 'Send',
        cancelText: "Don't Send",
        onOk: () => runImport({ sendWelcomeEmail: true }),
        onCancel: () => runImport({ sendWelcomeEmail: false }),
      });
    } else {
      runImport();
    }
  };

  const onBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prevStep) => prevStep - 1);
    }
  };

  const validateStep = () => {
    switch (currentStep) {
      case 0:
        return !!uploadFile;
      case 1:
        return !!selectedSheetName;
      case 2: {
        // Check if all required fields are mapped
        if (!effectiveHeaders.length) return false;
        const requiredHeaders = effectiveHeaders.filter((h) => h.required);
        const allRequiredMapped = requiredHeaders.every((header) => headerMapping[header.key]);

        // For buckets, also require bucket template to be selected
        if (importType === IMPORT_TYPES.BUCKETS && !selectedBucketTemplate) {
          return false;
        }

        return allRequiredMapped;
      }
      case 3:
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleRollbackComplete = () => {
    setImportResults((prev) => (prev ? { ...prev, success: [] } : null));
  };

  const handleResultsDone = () => {
    if (importType === IMPORT_TYPES.BUCKETS && selectedBucketTemplate) {
      history.push(`${importTypeConfig?.route}/${selectedBucketTemplate}`);
    } else {
      history.push(importTypeConfig?.route || '/dashboard');
    }
  };

  if (!importType || !importTypeConfig) {
    return (
      <BreadcrumbContainer crumbs={DEFAULT_CRUMBS} redesignCrumbStyle={{ marginLeft: 5 }}>
        <div style={{ padding: 24 }}>
          <h3>Invalid import type selected</h3>
          <OnTraccrButton
            title="Go Back"
            onClick={() => history.push('/dashboard')}
          />
        </div>
      </BreadcrumbContainer>
    );
  }

  return (
    <BreadcrumbContainer
      crumbs={[
        ...DEFAULT_CRUMBS,
        { text: importTypeConfig.label },
      ]}
      redesignCrumbStyle={{ marginLeft: 5 }}
    >
      <div style={{ height: '90%' }}>
        {uploadFile ? (
          <StepsContainer
            containerStyle={{
              height: '100%',
              backgroundColor: 'white',
              padding: 24,
              borderRadius: 12,
              border: '1px solid #E5E7EB',
              overflow: 'auto',
            }}
            currentStep={currentStep}
            steps={[
              {
                title: 'Upload File',
                content: (
                  <div>
                    <SheetUploadPrompt
                      onFileChange={onFileChange}
                      showUploadList={false}
                    />
                  </div>
                ),
              },
              {
                title: 'Select Sheet',
                content: (
                  <MassImportSheetSelector
                    workbook={uploadFile}
                    selectedSheetName={selectedSheetName}
                    onSheetChanged={setSelectedSheetName}
                  />
                ),
              },
              {
                title: 'Map Headers',
                content: (
                  <div>
                    {importType === IMPORT_TYPES.BUCKETS && (
                      <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#F9FAFB', borderRadius: 8 }}>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                          Bucket Template <span style={{ color: 'red', marginLeft: 4 }}>*</span>
                        </div>
                        <Select
                          value={selectedBucketTemplate}
                          onChange={setSelectedBucketTemplate}
                          style={{ width: '100%', maxWidth: 400 }}
                          placeholder="Select a bucket template"
                        >
                          {bucketTemplates.map((template) => (
                            <Select.Option key={template.id} value={template.id}>
                              {template.name}
                            </Select.Option>
                          ))}
                        </Select>
                        <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                          {selectedBucketTemplate
                            ? 'Custom fields for the selected template will appear below.'
                            : 'Please select a bucket template to continue.'}
                        </div>
                      </div>
                    )}
                    {importType === IMPORT_TYPES.PROJECTS && (
                      <OnTraccrCheckbox
                        value={useProjectCoordinates}
                        onChange={setUseProjectCoordinates}
                        style={{ paddingTop: 12, paddingBottom: 12 }}
                        label="Use coordinates for address field"
                        hoverNode="If you select this, the address field will be treated as the latitude and longitude coordinates instead of the address."
                        helpPlacement="right"
                      />
                    )}
                    <SheetHeaderMatcher
                      headerOptions={effectiveHeaders}
                      selectedSheet={selectedSheet}
                      headerMapping={headerMapping}
                      setHeaderMapping={setHeaderMapping}
                    />
                  </div>
                ),
              },
              {
                title: 'Preview',
                content: (
                  <MassImportPreview
                    selectedSheet={selectedSheet}
                    headerMapping={headerMapping}
                    importTypeConfig={importTypeConfig}
                    headers={effectiveHeaders}
                  />
                ),
              },
              {
                title: 'Results',
                content: importResults ? (
                  <MassImportResults
                    success={importResults.success}
                    failed={importResults.failed}
                    headers={effectiveHeaders}
                    importTypeConfig={importTypeConfig}
                    importType={importType}
                    onRollbackComplete={handleRollbackComplete}
                    onDone={handleResultsDone}
                  />
                ) : (
                  <div style={{ color: '#666' }}>
                    Complete the import from the Preview step to see results here.
                  </div>
                ),
              },
            ]}
          />
        ) : (
          <div
            style={{
              backgroundColor: 'white',
              padding: 24,
              borderRadius: 12,
              border: '1px solid #E5E7EB',
              height: '100%',
            }}
          >
            <SheetUploadPrompt onFileChange={onFileChange} />
          </div>
        )}
      </div>
      {uploadFile && currentStep < 4 && (
        <DrawerSubmitFooter
          onClose={currentStep > 0 ? onBack : null}
          closeTitle="Back"
          onSubmit={onNext}
          submitTitle={currentStep < 3 ? 'Next' : 'Import'}
          canSubmit={validateStep()}
          loading={loading}
        />
      )}
    </BreadcrumbContainer>
  );
}

MassImport.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      type: PropTypes.string,
    }),
  }),
};

MassImport.defaultProps = {
  match: null,
};

export default withScriptjs(MassImport);
